import React, { useState, useEffect } from 'react';
import { Percent, Plus, X, Pencil, Trash2, Handshake, Lock } from 'lucide-react';
import { useReferrerStore } from '../../lib/stores/referrerStore';
import { useApp } from '../AppProvider';
import { Portal } from '../ui/Portal';
import { supabase } from '../../lib/supabase';
import { hasPersonCommissionPayments } from '../../lib/utils/commissionPaymentCheck';
import type { Referrer, DealReferrer } from '../../lib/types';
import { formatReferrerName } from '../../lib/utils/referrerDisplayName';

interface PartnerWithReferrer extends Referrer {
  partner_name?: string;
  partner_company?: string;
}

/** Percentuale (o importo fisso) che il motore applica a un servizio dell'offerta. */
interface ServiceRate {
  label: string;
  rate: number;
  type: 'percentage' | 'fixed';
  fixedAmount: number | null;
}

const formatRate = (r: Pick<ServiceRate, 'rate' | 'type' | 'fixedAmount'>) =>
  r.type === 'fixed' ? `€${r.fixedAmount ?? r.rate ?? 0}` : `${r.rate ?? 0}%`;

interface DealReferrerSectionProps {
  dealId: string;
  onReferrersChanged?: (referrers: DealReferrer[]) => void;
}

export function DealReferrerSection({ dealId, onReferrersChanged }: DealReferrerSectionProps) {
  const { fetchDealReferrers, addDealReferrer, updateDealReferrer, removeDealReferrer, loading, dealReferrersLastUpdated } = useReferrerStore();
  const { toast, confirm } = useApp();
  const [dealReferrers, setDealReferrers] = useState<DealReferrer[]>([]);
  const [showSelectorModal, setShowSelectorModal] = useState(false);
  const [isEditingReferrer, setIsEditingReferrer] = useState<string | null>(null);
  const [customRate, setCustomRate] = useState<number | null>(null);
  const [partnersWithCommissions, setPartnersWithCommissions] = useState<PartnerWithReferrer[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  // Set di referrer_id con commissioni già pagate (bloccati)
  const [lockedReferrers, setLockedReferrers] = useState<Set<string>>(new Set());
  // Percentuali effettive per servizio, per segnalatore (stessa risoluzione del motore provvigioni)
  const [serviceRates, setServiceRates] = useState<Record<string, ServiceRate[]>>({});

  useEffect(() => {
    if (dealId) {
      loadDealReferrers();
      loadPartnersWithCommissions();
    }
  }, [dealId, dealReferrersLastUpdated]);

  // Verifica quali referrer hanno commissioni con pagamenti (bloccati)
  useEffect(() => {
    const checkLockedReferrers = async () => {
      if (!dealId || dealReferrers.length === 0) {
        setLockedReferrers(new Set());
        return;
      }

      try {
        // Recupera organization_id dal deal
        const { data: deal } = await supabase
          .from('deals')
          .select('organization_id')
          .eq('id', dealId)
          .single();

        if (!deal?.organization_id) return;

        const locked = new Set<string>();
        for (const dr of dealReferrers) {
          const hasPayments = await hasPersonCommissionPayments(
            dealId,
            dr.referrer_id,
            'referrer',
            deal.organization_id
          );
          if (hasPayments) {
            locked.add(dr.referrer_id);
          }
        }
        setLockedReferrers(locked);
      } catch (error) {
        console.error('Error checking locked referrers:', error);
      }
    };

    checkLockedReferrers();
  }, [dealId, dealReferrers]);

  // Percentuale mostrata = quella che il motore usa davvero, servizio per servizio:
  // resolve_commission_rate con deal e servizio (percentuale scritta sull'offerta, poi
  // regola servizio/gruppo segnalatori, poi base del segnalatore). Prima la colonna
  // mostrava la percentuale personale del segnalatore (es. 20%) anche quando il calcolo
  // applicava il 10% del servizio (segnalazione FinUp, fascicolo check-up).
  useEffect(() => {
    let cancelled = false;
    const loadServiceRates = async () => {
      if (!dealId || dealReferrers.length === 0) {
        setServiceRates({});
        return;
      }
      try {
        const [{ data: deal }, { data: services }] = await Promise.all([
          supabase.from('deals').select('organization_id').eq('id', dealId).single(),
          supabase
            .from('deal_services')
            .select('service_id, custom_service_name, services(title)')
            .eq('deal_id', dealId),
        ]);
        if (!deal?.organization_id) return;

        // Un'interrogazione per servizio di catalogo; le righe senza servizio usano la base
        const targets = new Map<string | null, string>();
        for (const ds of (services || []) as any[]) {
          const key = ds.service_id || null;
          if (!targets.has(key)) {
            targets.set(key, ds.services?.title || ds.custom_service_name || 'Altre voci');
          }
        }
        if (targets.size === 0) targets.set(null, 'Offerta');

        const result: Record<string, ServiceRate[]> = {};
        for (const dr of dealReferrers) {
          const rows = await Promise.all(
            Array.from(targets.entries()).map(async ([serviceId, label]) => {
              const { data, error } = await supabase.rpc('resolve_commission_rate', {
                p_organization_id: deal.organization_id,
                p_deal_id: dealId,
                p_service_id: serviceId,
                p_person_id: dr.referrer_id,
                p_person_type: 'referrer',
              });
              if (error || !data?.[0]) return null;
              const row = data[0];
              return {
                label,
                rate: Number(row.rate ?? 0),
                type: (row.commission_type === 'fixed' ? 'fixed' : 'percentage') as ServiceRate['type'],
                fixedAmount: row.fixed_amount != null ? Number(row.fixed_amount) : null,
              };
            }),
          );
          result[dr.referrer_id] = rows.filter((x): x is ServiceRate => x !== null);
        }
        if (!cancelled) setServiceRates(result);
      } catch (error) {
        console.error('Error loading referrer service rates:', error);
      }
    };
    loadServiceRates();
    return () => { cancelled = true; };
  }, [dealId, dealReferrers]);

  // Load all referrers (segnalatori) for the current organization
  const loadPartnersWithCommissions = async () => {
    setLoadingPartners(true);
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found');
      }

      const { data, error } = await supabase
        .from('referrers')
        .select(`
          *,
          external_partner:external_partners!referrers_external_partner_id_fkey(
            id, name, company
          )
        `)
        .eq('organization_id', profileData.organization_id);

      if (!error && data) {
        const mapped = data.map(r => ({
          ...r,
          partner_name: r.external_partner?.name,
          partner_company: r.external_partner?.company
        }));
        setPartnersWithCommissions(mapped);
      }
    } catch (error) {
      console.error('Error loading referrers:', error);
    }
    setLoadingPartners(false);
  };

  const loadDealReferrers = async () => {
    if (dealId) {
      const loadedReferrers = await fetchDealReferrers(dealId);
      setDealReferrers(loadedReferrers);
      if (onReferrersChanged) {
        onReferrersChanged(loadedReferrers);
      }
    }
  };

  const handleSelectPartner = async (partner: PartnerWithReferrer) => {
    // Check if partner is already added to the deal
    const isAlreadyAdded = dealReferrers.some(dr => dr.referrer_id === partner.id);

    if (isAlreadyAdded) {
      toast.info(`${partner.partner_name || formatReferrerName(partner.full_name)} è già assegnato a questa trattativa`);
      return;
    }

    try {
      await addDealReferrer(dealId, partner.id);
      toast.success(`${partner.partner_name || formatReferrerName(partner.full_name)} aggiunto come segnalatore`);
      loadDealReferrers();
      setShowSelectorModal(false);
    } catch (error) {
      toast.error('Errore nell\'aggiunta del segnalatore');
      console.error('Error adding referrer to deal:', error);
    }
  };

  const handleUpdateCommissionRate = async (dealReferrerId: string, rate: number) => {
    try {
      const result = await updateDealReferrer(dealReferrerId, { commission_rate: rate });
      if (!result) {
        toast.error('Errore nell\'aggiornamento della commissione');
        return;
      }
      toast.success('Commissione aggiornata');
      loadDealReferrers();
      setIsEditingReferrer(null);
      setCustomRate(null);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento della commissione');
      console.error('Error updating commission rate:', error);
    }
  };

  const handleRemovePartner = async (dealReferrer: DealReferrer) => {
    // Find referrer name from our loaded referrers
    const partnerInfo = partnersWithCommissions.find(p => p.id === dealReferrer.referrer_id);
    const displayName = partnerInfo?.partner_name || formatReferrerName(dealReferrer.referrer?.full_name) || 'Segnalatore';

    const confirmed = await confirm({
      title: 'Rimuovi Segnalatore',
      message: `Sei sicuro di voler rimuovere ${displayName} da questa trattativa?`,
      confirmText: 'Rimuovi',
      cancelText: 'Annulla',
      confirmVariant: 'danger'
    });

    if (confirmed) {
      try {
        await removeDealReferrer(dealReferrer.id);
        toast.success('Segnalatore rimosso dalla trattativa');
        loadDealReferrers();
      } catch (error) {
        toast.error('Errore nella rimozione del segnalatore');
        console.error('Error removing referrer:', error);
      }
    }
  };

  // Helper to get partner display name from deal referrer
  const getPartnerDisplayName = (dealReferrer: DealReferrer) => {
    const partnerInfo = partnersWithCommissions.find(p => p.id === dealReferrer.referrer_id);
    return partnerInfo?.partner_name || formatReferrerName(dealReferrer.referrer?.full_name) || 'Partner';
  };

  const getPartnerCompany = (dealReferrer: DealReferrer) => {
    const partnerInfo = partnersWithCommissions.find(p => p.id === dealReferrer.referrer_id);
    return partnerInfo?.partner_company;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Handshake className="h-5 w-5 text-[#787774] dark:text-gray-400" />
          <h3 className="text-sm font-medium text-[#37352F] dark:text-white uppercase tracking-wider">Segnalatori</h3>
        </div>
        <button
          onClick={() => setShowSelectorModal(true)}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-[#37352F] text-white hover:bg-[#2F2E2B] dark:bg-white dark:text-[#37352F] dark:hover:bg-gray-200 transition-colors"
        >
          <Plus className="h-4 w-4 mr-1" />
          Aggiungi segnalatore
        </button>
      </div>

      {dealReferrers.length === 0 ? (
        <div className="bg-[#F7F7F5] dark:bg-[#373C3F] border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] rounded-md p-4 text-center text-[#787774] dark:text-gray-400">
          Nessun segnalatore associato a questa trattativa.
        </div>
      ) : (
        <div className="overflow-x-auto border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] rounded-md">
          <table className="min-w-full divide-y divide-[rgba(55,53,47,0.09)] dark:divide-[#3F4447]">
            <thead className="bg-[#F7F7F5] dark:bg-[#373C3F]">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider">
                  Segnalatore
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider">
                  Contatto
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider">
                  Commissione
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#2F3437] divide-y divide-[rgba(55,53,47,0.09)] dark:divide-[#3F4447]">
              {dealReferrers.map((dealReferrer) => (
                <tr key={dealReferrer.id} className="hover:bg-[rgba(55,53,47,0.02)] dark:hover:bg-white/[0.02]">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-[#37352F] dark:text-white">
                      {getPartnerDisplayName(dealReferrer)}
                    </div>
                    {getPartnerCompany(dealReferrer) && (
                      <div className="text-xs text-[#787774] dark:text-gray-400">
                        {getPartnerCompany(dealReferrer)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[#787774] dark:text-gray-400">
                      {dealReferrer.referrer?.email}
                      {dealReferrer.referrer?.phone && (
                        <div>{dealReferrer.referrer.phone}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap ">
                    {isEditingReferrer === dealReferrer.id ? (
                      <div className="flex items-center">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          className="w-20 px-2 py-1 bg-[#F7F7F5] dark:bg-[#373C3F] border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] rounded-md text-[#37352F] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
                          value={customRate !== null ? customRate : (dealReferrer.commission_rate || 0)}
                          onChange={(e) => setCustomRate(parseFloat(e.target.value) || 0)}
                        />
                        <span className="ml-1 text-[#37352F] dark:text-white">%</span>
                        <button
                          onClick={() => handleUpdateCommissionRate(dealReferrer.id, customRate !== null ? customRate : (dealReferrer.commission_rate || 0))}
                          className="ml-2 text-[#37352F] dark:text-white hover:text-[#787774] dark:hover:text-gray-300 transition-colors"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingReferrer(null);
                            setCustomRate(null);
                          }}
                          className="ml-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        {lockedReferrers.has(dealReferrer.referrer_id) ? (
                          <Lock className="h-4 w-4 text-amber-500 dark:text-amber-400 mr-1" />
                        ) : (
                          <Percent className="h-4 w-4 text-[#787774] dark:text-gray-400 mr-1" />
                        )}
                        {(() => {
                          const rates = serviceRates[dealReferrer.referrer_id];
                          // Finche' le percentuali effettive non arrivano: valore scritto sull'offerta, se c'e'
                          if (!rates || rates.length === 0) {
                            return (
                              <span className="text-sm text-[#37352F] dark:text-white">
                                {dealReferrer.commission_rate != null ? `${dealReferrer.commission_rate}%` : '…'}
                              </span>
                            );
                          }
                          const distinct = Array.from(new Set(rates.map(formatRate)));
                          if (distinct.length === 1) {
                            return <span className="text-sm text-[#37352F] dark:text-white">{distinct[0]}</span>;
                          }
                          // Percentuali diverse fra i servizi: una riga per servizio
                          return (
                            <div className="space-y-0.5">
                              {rates.map((sr) => (
                                <div key={sr.label} className="text-sm text-[#37352F] dark:text-white">
                                  <span className="text-[#787774] dark:text-gray-400">{sr.label}</span>
                                  {' · '}
                                  {formatRate(sr)}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                        {!lockedReferrers.has(dealReferrer.referrer_id) && (
                          <button
                            onClick={() => {
                              setIsEditingReferrer(dealReferrer.id);
                              // Proposta: la percentuale scritta sull'offerta, altrimenti quella effettiva
                              // se uguale per tutti i servizi (mai la percentuale personale del segnalatore)
                              const rates = serviceRates[dealReferrer.referrer_id] || [];
                              const uniform = rates.length > 0 && rates.every(x => x.type === 'percentage' && x.rate === rates[0].rate)
                                ? rates[0].rate
                                : null;
                              setCustomRate(dealReferrer.commission_rate ?? uniform ?? 0);
                            }}
                            className="ml-2 text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white transition-colors"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {lockedReferrers.has(dealReferrer.referrer_id) ? (
                      <span className="text-xs text-amber-600 dark:text-amber-400" title="Commissione con pagamenti registrati">
                        Bloccato
                      </span>
                    ) : (
                      <button
                        onClick={() => handleRemovePartner(dealReferrer)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Partner Selection Modal */}
      {showSelectorModal && (
        <Portal>
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-[#2F3437] border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] rounded-lg p-6 w-full max-w-2xl shadow-xl max-h-[80vh] flex flex-col">
              <div className="flex justify-between items-center mb-4 pb-3 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                <div className="flex items-center gap-2">
                  <Handshake className="h-5 w-5 text-[#787774] dark:text-gray-400" />
                  <h3 className="text-sm font-medium text-[#37352F] dark:text-white">Seleziona un segnalatore</h3>
                </div>
                <button
                  onClick={() => setShowSelectorModal(false)}
                  className="text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {loadingPartners ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#37352F] dark:border-white"></div>
                </div>
              ) : partnersWithCommissions.length === 0 ? (
                <div className="text-center py-8 text-[#787774] dark:text-gray-400">
                  <Handshake className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Nessun segnalatore disponibile.</p>
                  <p className="text-sm mt-1">Crea un segnalatore dalla sezione Segnalatori.</p>
                </div>
              ) : (
                <div className="overflow-y-auto flex-1">
                  <div className="space-y-1">
                    {partnersWithCommissions
                      .filter(p => !dealReferrers.some(dr => dr.referrer_id === p.id))
                      .map((partner) => (
                        <button
                          key={partner.id}
                          onClick={() => handleSelectPartner(partner)}
                          className="w-full p-3 text-left rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04] transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-[#37352F] dark:text-white">
                                {partner.partner_name || formatReferrerName(partner.full_name)}
                              </div>
                              {partner.partner_company && (
                                <div className="text-sm text-[#787774] dark:text-gray-400">
                                  {partner.partner_company}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-sm text-[#37352F] dark:text-white font-medium">
                              <Percent className="h-4 w-4 text-[#787774] dark:text-gray-400" />
                              <span>{partner.commission_rate}%</span>
                            </div>
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}